import { ImageObject, ItemList, NewsArticle, Thing, WebPage, WithContext } from 'schema-dts';
import { Article } from '@/entities/article';
import type { AppBreadcrumbs } from '@/shared/components/shared/Breadcrumbs';
import { ddMmYyyyToIso8601Date } from '@/shared/lib/utils/dates';
import { composeSchemaGraph } from '@/shared/lib/utils/page-json-ld';
import { oneLine, schemaIdRef } from '@/shared/lib/utils/schema-common';
import {
    SCHEMA_ORGANIZATION_NAME,
    getSchemaIds,
    schemaAbsoluteUrl,
    schemaPageUrl,
} from '@/shared/lib/utils/schema-ids';

export interface NewsJsonLdOptions {
    h1?: string;
    pageDescription?: string;
    listingPath: string;
    articles: Article[];
    breadcrumbs?: AppBreadcrumbs | null;
}

function normalizeListingPath(listingPath: string): string {
    const withSlash = listingPath.startsWith('/') ? listingPath : `/${listingPath}`;
    return withSlash.endsWith('/') ? withSlash : `${withSlash}/`;
}

function articlePublicPath(article: Article): string {
    const primaryCategory = article.categories.find((c) => c.slug) || article.categories[0];
    if (primaryCategory?.slug) {
        return `/news/${primaryCategory.slug}/${article.slug}/`;
    }
    return `/news/${article.slug}/`;
}

export function generateNewsJsonLd({
    h1,
    pageDescription,
    listingPath,
    articles,
    breadcrumbs,
}: NewsJsonLdOptions): WithContext<Thing>[] {
    const ids = getSchemaIds();
    const listingUrl = schemaPageUrl(normalizeListingPath(listingPath));
    const topArticles = articles.slice(0, 10);
    const publisherLogoUrl = schemaAbsoluteUrl('/img/logo.svg');
    const descriptionText = pageDescription ? oneLine(pageDescription) : '';

    const articleItems: NewsArticle[] = topArticles.map((article) => {
        const articleUrl = schemaAbsoluteUrl(articlePublicPath(article));
        const image: ImageObject | undefined = article.img?.src
            ? {
                  '@type': 'ImageObject',
                  url: schemaAbsoluteUrl(article.img.src),
                  ...(article.img.width != null && { width: String(article.img.width) }),
                  ...(article.img.height != null && { height: String(article.img.height) }),
              }
            : undefined;

        return {
            '@type': 'NewsArticle',
            headline: article.title,
            datePublished: ddMmYyyyToIso8601Date(article.date) ?? article.date,
            url: articleUrl,
            image,
            author: schemaIdRef(ids.organization) as NewsArticle['author'],
            publisher: {
                '@type': 'Organization',
                '@id': ids.organization,
                name: SCHEMA_ORGANIZATION_NAME,
                logo: {
                    '@type': 'ImageObject',
                    url: publisherLogoUrl,
                },
            },
        };
    });

    const itemList: ItemList = {
        '@type': 'ItemList',
        name: h1 || 'Новости',
        ...(descriptionText ? { description: descriptionText } : {}),
        numberOfItems: articleItems.length,
        itemListElement: articleItems.map((article, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: article,
        })),
    };

    const webPage: WithContext<WebPage> = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${listingUrl}#webpage`,
        name: h1 || 'Новости',
        ...(descriptionText ? { description: descriptionText } : {}),
        url: listingUrl,
        isPartOf: schemaIdRef(ids.website) as WebPage['isPartOf'],
        publisher: schemaIdRef(ids.organization) as WebPage['publisher'],
        ...(articleItems.length > 0 ? { mainEntity: itemList } : {}),
    };

    return composeSchemaGraph({
        nodes: [webPage as WithContext<Thing>],
        breadcrumbs,
    });
}
